import type { ComponentTypesV2 } from 'types/selectComponent'
import BasicFilter from '../basic/BasicFilter'
import type {
  ConfigurableFilterOptionsType,
  FilterComponentNodeV2,
  FilterGenericPropsType,
  FilterValuesType,
} from '../typeV2'
import { parseStringifiedListFilterValue } from '../utils/utils'
import { CABLE_THICKNESS_OPTIONS } from './CableThicknessFilter'

export const CABLE_THICKNESS_FILTER_KEY_V2 = 'cable_thickness'

export const isCableThicknessFilterAvailableV2 = (filterValues: FilterValuesType) => {
  const types = parseStringifiedListFilterValue<ComponentTypesV2>({
    filterValues,
    key: 'types',
  })
  return types.has('cable')
}

const createCableThicknessFilterNode = <T,>({
  label = 'Cable Thickness',
  ...restConfigs
}: ConfigurableFilterOptionsType<string, T>): FilterComponentNodeV2 => {
  const FilterComponent = (filterProps: FilterGenericPropsType) => {
    const isAvailable = isCableThicknessFilterAvailableV2(filterProps.allFilterValues)
    if (!isAvailable) {
      return null
    }

    const explicitThicknessValue = filterProps.allFilterValues?.[CABLE_THICKNESS_FILTER_KEY_V2]
    const parseExplicitThicknessSelection = (value: unknown): string[] => {
      if (value instanceof Set) {
        return Array.from(value)
          .map((item) => String(item))
          .filter((item) => item.length > 0)
      }
      if (Array.isArray(value)) {
        return value.map((item) => String(item)).filter((item) => item.length > 0)
      }
      if (typeof value === 'number') {
        return [String(value)]
      }
      if (typeof value === 'string') {
        if (!value.trim()) return []
        try {
          const parsed = JSON.parse(value)
          if (Array.isArray(parsed)) {
            return parsed.map((item) => String(item)).filter((item) => item.length > 0)
          }
          if (parsed != null && parsed !== '') {
            return [String(parsed)]
          }
        } catch (_) {
          // Non-JSON scalar string, keep as a single selection.
          return [value]
        }
      }
      return []
    }

    const explicitThicknessSelection = parseExplicitThicknessSelection(explicitThicknessValue)
    const thicknessInValue = filterProps.allFilterValues?.['cable_thickness__in']
    const thicknessFromIn = typeof thicknessInValue === 'string'
      ? thicknessInValue
          .split(',')
          .map((value) => String(value).trim())
          .filter((value) => value.length > 0)
      : []
    const thicknessFromGte = Number(filterProps.allFilterValues?.['cable_thickness__gte'])
    const gteBasedDefaultSelection =
      explicitThicknessSelection.length === 0 && !Number.isNaN(thicknessFromGte)
        ? CABLE_THICKNESS_OPTIONS.filter(
            (option) => option.value !== 'all' && Number(option.value) >= thicknessFromGte
          ).map((option) => option.value)
        : []
    const defaultSelectionFromFilters =
      explicitThicknessSelection.length > 0
        ? explicitThicknessSelection
        : thicknessFromIn.length > 0
        ? thicknessFromIn
        : gteBasedDefaultSelection
    const cableThicknessSideEffect = ({
      filterValues,
      value,
    }: {
      filterValues: FilterValuesType
      modifiableFilterValues: FilterValuesType
      value: unknown
      selectedOption: unknown
    }) => {
      const nextFilterValues = { ...filterValues }
      const rawValues =
        value instanceof Set ? Array.from(value) : Array.isArray(value) ? value : value != null ? [value] : []
      const selectedThicknesses = rawValues
        .map((item) => String(item))
        .filter((item) => item.length > 0 && item !== 'all')
      if (selectedThicknesses.length > 0) {
        nextFilterValues['cable_thickness__in'] = selectedThicknesses.join(',')
      } else {
        delete nextFilterValues['cable_thickness__in']
      }
      delete nextFilterValues[CABLE_THICKNESS_FILTER_KEY_V2]
      return nextFilterValues
    }

    return (
      <BasicFilter
        label={label}
        defaultSelectedOptionValue={new Set<string>(defaultSelectionFromFilters)}
        options={CABLE_THICKNESS_OPTIONS}
        filterKey={CABLE_THICKNESS_FILTER_KEY_V2}
        sideEffect={cableThicknessSideEffect}
        {...restConfigs}
        {...filterProps}
      />
    )
  }

  return {
    key: CABLE_THICKNESS_FILTER_KEY_V2,
    component: FilterComponent,
  }
}

export default createCableThicknessFilterNode
